/**
 * **R1-5(1) — the app's last line of defence against a white screen.**
 *
 * React unmounts the whole tree when a render throws and nothing catches it, so before this a
 * single bad render anywhere below `App` left an empty document: no bar, no drawer, no text, and
 * no way back except a reload the user has no reason to think of. There was no boundary and no
 * `componentDidCatch` in the repository at all.
 *
 * **It is a backstop, not the fix.** A boundary that catches a stranded screen still loses the
 * user's place; what stops the throw is the guard at the call site. What this buys is that the
 * failure is *visible* and *leaveable*.
 *
 * Three deliberate shapes:
 *
 *  - **The copy is translated.** The single most likely reader is a Hebrew-speaking resident, and
 *    an English developer string here would be the second-worst thing on the screen after the
 *    blank one.
 *  - **The action is not "reload" alone.** A reload re-runs the same render against the same
 *    state; if the state is what threw, the user gets the boundary again and has learned that the
 *    app is broken rather than that a screen is. So there is *Try again* (which re-mounts the
 *    subtree) and, beneath it, the **sign-out backstop `NameStep` already offers** — the honest
 *    floor, because a session that can be restarted is never a dead end.
 *  - **The sign-out row is conditional on a provider being mounted.** `useSignOut()` throws
 *    without one, and a fallback that throws is a boundary that produces the white screen it
 *    exists to prevent.
 */

import { Component, useContext, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from '../i18n/I18nProvider'
import { PrimaryButton, LinkButton } from './Button'
import { SignOutContext } from './auth/context'

export function ErrorFallback({ onRetry }: { readonly onRetry: () => void }): JSX.Element {
  const { t } = useTranslation('common')
  // Deliberately `useContext` rather than `useSignOut()`: the hook throws with no provider, and
  // this component must render under every condition, including the ones that got it here.
  const signOut = useContext(SignOutContext)

  return (
    <div className="app-screen no-bar" role="alert" style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 'var(--font-header)', fontWeight: 700, marginBlockEnd: 8 }}>
        {t('errorTitle')}
      </div>
      <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)', marginBlockEnd: 20 }}>
        {t('errorBody')}
      </div>
      <div className="form-row">
        <PrimaryButton onClick={onRetry}>{t('retry')}</PrimaryButton>
      </div>
      {signOut === undefined ? null : (
        <div className="hint centered">
          <LinkButton onClick={signOut}>{t('errorSignOut')}</LinkButton>
        </div>
      )}
    </div>
  )
}

interface ErrorBoundaryProps {
  readonly children: ReactNode
  /**
   * Changing it clears a caught error. `App` passes the route id, so **navigating away is itself
   * a recovery** — without it a single throw would pin the boundary over every later route, which
   * is a worse failure than the one being caught.
   */
  readonly resetKey?: string
  /** Test seam. Production has no reporter; `componentDidCatch` logs and that is the whole of it. */
  readonly onError?: (error: unknown, componentStack: string) => void
}

interface ErrorBoundaryState {
  readonly caught: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { caught: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { caught: true }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // **Logged, never swallowed.** The boundary is the second half of R1-5's telemetry seam: the
    // `unhandledrejection` listener in `main.tsx` catches what escapes a handler, and this catches
    // what escapes a render. A boundary that renders a friendly message and drops the error is
    // how a defect survives a whole release.
    console.error('render-error', error, info.componentStack)
    this.props.onError?.(error, info.componentStack ?? '')
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.caught && previous.resetKey !== this.props.resetKey) this.setState({ caught: false })
  }

  override render(): ReactNode {
    if (!this.state.caught) return this.props.children
    return <ErrorFallback onRetry={() => this.setState({ caught: false })} />
  }
}

/**
 * **R1-5(2) — the other half of the same backstop, which is why it lives beside it.**
 *
 * The boundary above catches what escapes a *render*; this catches what escapes a *handler*.
 * Every `void someAsyncThing()` in the app sends its rejection nowhere — a React event handler
 * cannot be `async` — so before this a failed request behind a tapped button was not merely
 * stranded but **invisible**: nothing logged, nothing rendered, and the only symptom was a
 * control that stopped responding. That is why the unguarded call sites went unnoticed for the
 * life of the screens.
 *
 * **It logs and does not swallow.** No `preventDefault()`: the browser still reports the
 * rejection itself, and any future reporter still sees it. Making it a handler — resetting a
 * flag, showing a message — would let a screen ship without its own guard, which is the outcome
 * this whole PRD exists to prevent.
 *
 * Takes its target the way `bindOnlineRecovery` does, so the listener is reachable from a test
 * without dispatching a `PromiseRejectionEvent` jsdom may not construct.
 */
export function installUnhandledRejectionLogging(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const report = (event: Event): void => {
    console.error('unhandled-rejection', (event as PromiseRejectionEvent).reason)
  }
  target.addEventListener('unhandledrejection', report)
  return () => target.removeEventListener('unhandledrejection', report)
}
